import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import Game from './components/Game';
import HomePage from './components/HomePage';
import UnlimitedGame from './components/UnlimitedGame';
import ZoomedInGenerationsGame from './components/games/generations/ZoomedInGenerationsGame';
import RiddledInGame from './components/games/riddledIn/RiddledInGame';
import ZoomedInEarthGame from './components/games/earth/ZoomedInEarthGame';
import ZoomedInDailyGame from './components/games/daily/ZoomedInDailyGame';
import ZoomedInCustomCreatePage from './components/games/custom/ZoomedInCustomCreatePage';
import ZoomedInCustomPlayPage from './components/games/custom/ZoomedInCustomPlayPage';
import DailyPuzzleAdminPage from './components/admin/DailyPuzzleAdminPage';
import HowToPlay from './components/HowToPlay';
import AuthPage from './components/auth/AuthPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import { ProfileProvider, useProfile } from './contexts/ProfileContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import CoinDisplay from './components/CoinDisplay';
import UserMenu from './components/account/UserMenu';
import Footer from './components/layout/Footer';
import AboutPage from './components/pages/AboutPage';
import ContactPage from './components/pages/ContactPage';
import PrivacyPolicyPage from './components/pages/PrivacyPolicyPage';
import TermsOfServicePage from './components/pages/TermsOfServicePage';
import CoinShopPage from './components/pages/CoinShopPage';
import NotFoundPage from './components/pages/NotFoundPage';
import RouteSeo from './components/seo/RouteSeo';
import Leaderboard from './components/Leaderboard';
import { LEADERBOARD_HEADER_TITLE } from './constants/leaderboard';
import DailyCheckinRewardModal from './components/points/DailyCheckinRewardModal';
import UnlimitedMobileLayoutToggleButton from './components/games/unlimited/UnlimitedMobileLayoutToggleButton';
import GenerationsMobileHeaderMenu from './components/games/generations/GenerationsMobileHeaderMenu';
import './App.css';
import './styles/headerPillButton.css';
import { useMediaQuery } from './utils/hooks/useMediaQuery';
import {
  loadUnlimitedMobileSideBySidePreference,
  saveUnlimitedMobileSideBySidePreference,
} from './utils/games/unlimitedLayoutPreference';
import {
  loadGenerationsMobileSideBySidePreference,
  saveGenerationsMobileSideBySidePreference,
} from './utils/games/generationsLayoutPreference';
import {
  loadDailyMobileSideBySidePreference,
  saveDailyMobileSideBySidePreference,
} from './utils/games/dailyLayoutPreference';
import {
  loadCustomMobileSideBySidePreference,
  saveCustomMobileSideBySidePreference,
} from './utils/games/customLayoutPreference';

/* Inner shell that has access to ThemeContext (must be a child of ThemeProvider) */
function AppShell() {
  const { theme } = useTheme();
  const [showHelp, setShowHelp] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [unlimitedSideBySide, setUnlimitedSideBySide] = useState(() => {
    const saved = loadUnlimitedMobileSideBySidePreference();
    if (typeof saved === 'boolean') return saved;
    // Default: on mobile, start in split view.
    return isMobile;
  });

  const [generationsSideBySide, setGenerationsSideBySide] = useState(() => {
    const saved = loadGenerationsMobileSideBySidePreference();
    if (typeof saved === 'boolean') return saved;
    // Default: on mobile, start in split view.
    return isMobile;
  });

  const [dailySideBySide, setDailySideBySide] = useState(() => {
    const saved = loadDailyMobileSideBySidePreference();
    if (typeof saved === 'boolean') return saved;
    // Default: on mobile, start in split view.
    return isMobile;
  });
  const [customSideBySide, setCustomSideBySide] = useState(() => {
    const saved = loadCustomMobileSideBySidePreference();
    if (typeof saved === 'boolean') return saved;
    // Default: on mobile, start in split view.
    return isMobile;
  });
  const [dailyHowToOpenRequest, setDailyHowToOpenRequest] = useState(0);

  // Desktop should never use side-by-side layout.
  useEffect(() => {
    if (!isMobile && unlimitedSideBySide) {
      setUnlimitedSideBySide(false);
    }
  }, [isMobile, unlimitedSideBySide]);

  useEffect(() => {
    if (!isMobile && generationsSideBySide) {
      setGenerationsSideBySide(false);
    }
  }, [isMobile, generationsSideBySide]);

  useEffect(() => {
    if (!isMobile && dailySideBySide) {
      setDailySideBySide(false);
    }
  }, [dailySideBySide, isMobile]);

  useEffect(() => {
    if (!isMobile && customSideBySide) {
      setCustomSideBySide(false);
    }
  }, [customSideBySide, isMobile]);

  // When entering mobile, apply saved preference; otherwise default to split view.
  useEffect(() => {
    if (!isMobile) return;
    const saved = loadUnlimitedMobileSideBySidePreference();
    setUnlimitedSideBySide(typeof saved === 'boolean' ? saved : true);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    const saved = loadGenerationsMobileSideBySidePreference();
    setGenerationsSideBySide(typeof saved === 'boolean' ? saved : true);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    const saved = loadDailyMobileSideBySidePreference();
    setDailySideBySide(typeof saved === 'boolean' ? saved : true);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    const saved = loadCustomMobileSideBySidePreference();
    setCustomSideBySide(typeof saved === 'boolean' ? saved : true);
  }, [isMobile]);

  return (
      <Router>
        <RouteSeo />
        <div className={`App${theme === 'light' ? ' App--light' : ''}`}>
          {showHelp && <HowToPlay onClose={() => setShowHelp(false)} />}
          <main className="app-main">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/play" element={
                <>
                  <header className="game-header">
                    <Link to="/" className="back-button">←</Link>
                    <h1>Zoomed In</h1>
                    <div className="header-right">
                      <button
                        onClick={() => setShowHelp(true)}
                        className="help-button header-pill-button"
                        aria-label="Info"
                      >
                        Info
                      </button>
                      <UserMenu />
                    </div>
                  </header>
                  <Game />
                </>
              } />
              <Route path="/hardcore" element={
                <>
                  <header className="game-header">
                    <Link to="/" className="back-button">←</Link>
                    <h1>Zoomed In Hardcore</h1>
                    <div className="header-right">
                      <button
                        onClick={() => setShowHelp(true)}
                        className="help-button header-pill-button"
                        aria-label="Info"
                      >
                        Info
                      </button>
                      <UserMenu />
                    </div>
                  </header>
                  <Game isHardcore={true} />
                </>
              } />
              <Route path="/unlimited" element={
                <>
                  <header className="game-header">
                    <Link to="/" className="back-button">←</Link>
                    {isMobile ? (
                      <div className="game-header-center" aria-hidden="true" />
                    ) : (
                      <h1>Zoomed In Unlimited</h1>
                    )}
                    <div className="header-right">
                      <CoinDisplay />
                      {isMobile && (
                        <UnlimitedMobileLayoutToggleButton
                          isSideBySide={unlimitedSideBySide}
                          onToggle={() => {
                            setUnlimitedSideBySide((v) => {
                              const next = !v;
                              saveUnlimitedMobileSideBySidePreference(next);
                              return next;
                            });
                          }}
                        />
                      )}
                      <button
                        onClick={() => setShowHelp(true)}
                        className="help-button header-pill-button"
                        aria-label="Info"
                      >
                        Info
                      </button>
                      <UserMenu hideSignedInName />
                    </div>
                  </header>
                  {isMobile && <div className="below-header-title">Zoomed In Unlimited</div>}
                  <UnlimitedGame mobileSideBySide={isMobile && unlimitedSideBySide} />
                </>
              } />
              <Route path="/generations" element={
                <>
                  <header className="game-header">
                    <Link to="/" className="back-button">←</Link>
                    {isMobile ? (
                      <div className="game-header-center" aria-hidden="true" />
                    ) : (
                      <h1>Zoomed In Generations</h1>
                    )}
                    <div className="header-right">
                      <CoinDisplay />
                      {isMobile ? (
                        <>
                          <UnlimitedMobileLayoutToggleButton
                            isSideBySide={generationsSideBySide}
                            onToggle={() => {
                              setGenerationsSideBySide((v) => {
                                const next = !v;
                                saveGenerationsMobileSideBySidePreference(next);
                                return next;
                              });
                            }}
                          />
                          <GenerationsMobileHeaderMenu onInfo={() => setShowHelp(true)} />
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setShowHelp(true)}
                            className="help-button header-pill-button"
                            aria-label="Info"
                          >
                            Info
                          </button>
                          <UserMenu hideSignedInName />
                        </>
                      )}
                    </div>
                  </header>
                  {isMobile && <div className="below-header-title">Zoomed In Generations</div>}
                  <ZoomedInGenerationsGame mobileSideBySide={isMobile && generationsSideBySide} />
                </>
              } />
              <Route path="/riddled-in" element={
                <>
                  <header className="game-header">
                    <Link to="/" className="back-button">←</Link>
                    <h1>Riddled-In</h1>
                    <div className="header-right">
                      <CoinDisplay />
                      <UserMenu hideSignedInName />
                    </div>
                  </header>
                  <RiddledInGame />
                </>
              } />
              <Route path="/earth" element={
                <>
                  <header className="game-header">
                    <Link to="/" className="back-button">←</Link>
                    <h1>Zoomed In Earth</h1>
                    <div className="header-right">
                      <CoinDisplay />
                      <UserMenu hideSignedInName />
                    </div>
                  </header>
                  <ZoomedInEarthGame />
                </>
              } />
              <Route path="/daily" element={
                <>
                  <header className="game-header">
                    <Link to="/" className="back-button">←</Link>
                    {isMobile ? (
                      <div className="game-header-center" aria-hidden="true" />
                    ) : (
                      <h1>Zoomed In Daily</h1>
                    )}
                    <div className="header-right">
                      <CoinDisplay />
                      {isMobile && (
                        <UnlimitedMobileLayoutToggleButton
                          isSideBySide={dailySideBySide}
                          onToggle={() => {
                            setDailySideBySide((v) => {
                              const next = !v;
                              saveDailyMobileSideBySidePreference(next);
                              return next;
                            });
                          }}
                        />
                      )}
                      <button
                        onClick={() => setDailyHowToOpenRequest((previous) => previous + 1)}
                        className="help-button header-pill-button daily-help-button"
                        aria-label="How To Play"
                      >
                        ?
                      </button>
                      <UserMenu hideSignedInName />
                    </div>
                  </header>
                  {isMobile && <div className="below-header-title">Zoomed In Daily</div>}
                  <ZoomedInDailyGame
                    mobileSideBySide={isMobile && dailySideBySide}
                    openHowToRequest={dailyHowToOpenRequest}
                  />
                </>
              } />
              <Route path="/custom/create" element={
                <>
                  <header className="game-header">
                    <Link to="/" className="back-button">←</Link>
                    <h1>Zoomed In Custom</h1>
                    <div className="header-right">
                      <CoinDisplay />
                      <UserMenu hideSignedInName />
                    </div>
                  </header>
                  <ZoomedInCustomCreatePage />
                </>
              } />
              <Route path="/custom/:slug" element={
                <>
                  <header className="game-header">
                    <Link to="/" className="back-button">←</Link>
                    {isMobile ? (
                      <div className="game-header-center" aria-hidden="true" />
                    ) : (
                      <h1>Zoomed In Custom</h1>
                    )}
                    <div className="header-right">
                      {isMobile && (
                        <UnlimitedMobileLayoutToggleButton
                          isSideBySide={customSideBySide}
                          onToggle={() => {
                            setCustomSideBySide((v) => {
                              const next = !v;
                              saveCustomMobileSideBySidePreference(next);
                              return next;
                            });
                          }}
                        />
                      )}
                      <UserMenu hideSignedInName />
                    </div>
                  </header>
                  {isMobile && <div className="below-header-title">Zoomed In Custom</div>}
                  <ZoomedInCustomPlayPage mobileSideBySide={isMobile && customSideBySide} />
                </>
              } />
              <Route path="/admin/daily" element={
                <>
                  <header className="game-header">
                    <Link to="/" className="back-button">←</Link>
                    <h1>Daily Puzzle Admin</h1>
                    <div className="header-right">
                      <UserMenu hideSignedInName />
                    </div>
                  </header>
                  <DailyPuzzleAdminPage />
                </>
              } />
              <Route path="/leaderboard" element={
                <>
                  <header className="game-header">
                    <Link to="/" className="back-button">←</Link>
                    <h1>{LEADERBOARD_HEADER_TITLE}</h1>
                    <div className="header-right">
                      <UserMenu hideSignedInName />
                    </div>
                  </header>
                  <Leaderboard />
                </>
              } />
              <Route path="/auth" element={
                <>
                  <header className="game-header">
                    <Link to="/" className="back-button">←</Link>
                    <h1>Account</h1>
                    <div className="header-right">
                      <UserMenu />
                    </div>
                  </header>
                  <AuthPage />
                </>
              } />
              <Route path="/auth/forgot" element={
                <>
                  <header className="game-header">
                    <Link to="/auth" className="back-button">←</Link>
                    <h1>Reset Password</h1>
                    <div className="header-right">
                      <UserMenu />
                    </div>
                  </header>
                  <ForgotPasswordPage />
                </>
              } />
              <Route path="/auth/reset" element={
                <>
                  <header className="game-header">
                    <Link to="/auth" className="back-button">←</Link>
                    <h1>Reset Password</h1>
                    <div className="header-right">
                      <UserMenu />
                    </div>
                  </header>
                  <ResetPasswordPage />
                </>
              } />
              <Route
                path="/about"
                element={
                  <>
                    <header className="game-header">
                      <Link to="/" className="back-button">
                        ←
                      </Link>
                      <h1>About</h1>
                      <div className="header-right">
                        <UserMenu />
                      </div>
                    </header>
                    <AboutPage />
                  </>
                }
              />
              <Route
                path="/contact"
                element={
                  <>
                    <header className="game-header">
                      <Link to="/" className="back-button">
                        ←
                      </Link>
                      <h1>Contact</h1>
                      <div className="header-right">
                        <UserMenu />
                      </div>
                    </header>
                    <ContactPage />
                  </>
                }
              />
              <Route
                path="/privacy"
                element={
                  <>
                    <header className="game-header">
                      <Link to="/" className="back-button">
                        ←
                      </Link>
                      <h1>Privacy</h1>
                      <div className="header-right">
                        <UserMenu />
                      </div>
                    </header>
                    <PrivacyPolicyPage />
                  </>
                }
              />
              <Route
                path="/terms"
                element={
                  <>
                    <header className="game-header">
                      <Link to="/" className="back-button">
                        ←
                      </Link>
                      <h1>Terms</h1>
                      <div className="header-right">
                        <UserMenu />
                      </div>
                    </header>
                    <TermsOfServicePage />
                  </>
                }
              />
              <Route
                path="/coins"
                element={
                  <>
                    <header className="game-header">
                      <Link to="/" className="back-button">
                        ←
                      </Link>
                      <h1>Coins</h1>
                      <div className="header-right">
                        <CoinDisplay />
                        <UserMenu />
                      </div>
                    </header>
                    <CoinShopPage />
                  </>
                }
              />
              <Route
                path="*"
                element={
                  <>
                    <header className="game-header">
                      <Link to="/" className="back-button">
                        ←
                      </Link>
                      <h1>Page Not Found</h1>
                      <div className="header-right">
                        <UserMenu />
                      </div>
                    </header>
                    <NotFoundPage />
                  </>
                }
              />
            </Routes>
          </main>
          <Footer />
          <DailyCheckinRewardGate />
        </div>
      </Router>
  );
}

const DailyCheckinRewardGate = () => {
  const { dailyCheckinReward, dismissDailyCheckinReward } = useProfile();

  return (
    <DailyCheckinRewardModal
      reward={dailyCheckinReward}
      onClose={dismissDailyCheckinReward}
    />
  );
};

function App() {
  return (
    <ProfileProvider>
      <ThemeProvider>
        <AppShell />
      </ThemeProvider>
    </ProfileProvider>
  );
}

export default App; 
